import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { forgetTrustTokens } from "../../auth/trustedBrowser";
import {
  addOwnMobile,
  contactErrorText,
  getContactDetails,
  sendAddMobileCode,
  looksLikeEmail,
  sendEmailCheckCode,
  setOwnEmail,
  verifyOwnMobile,
} from "../../api/account";
import { maskMobile, normalizeIndianMobile, testCodeAccessToken } from "../../api/login";
import { sendAccountCode } from "../../api/password";
import { useResendTimer } from "../../components/useResendTimer";

const verifiedChip = (ok: boolean, testId: string) =>
  ok ? <Chip size="small" color="success" label="Verified" data-testid={testId} /> : <Chip size="small" color="warning" label="Not verified" data-testid={testId} />;

const codeInput = (value: string, set: (v: string) => void, testId: string) => (
  <TextField
    label="6-digit code"
    value={value}
    onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 6))}
    autoComplete="one-time-code"
    size="small"
    slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": testId } }}
    sx={{ maxWidth: 200 }}
  />
);

/**
 * Settings › Sign-in & security › My contact details (owner 2026-10-25: "I
 * don't know even mobile number and don't know where it is saved"). The
 * signed-in person's OWN mobile and email as ZITADEL holds them.
 *
 * - Mobile: shown; a stored but unverified number is verified with an SMS code
 *   to exactly that number. A different number is an administrator's change.
 * - Email: added or changed with a code emailed to the new address; once
 *   verified, the Email and Both code choices work on every code screen.
 *
 * 🔑 Always the token's own namespace, as ChangePasswordCard.
 */
export function ContactDetailsCard() {
  const identity = useIdentity();
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const qc = useQueryClient();
  const contact = useQuery({
    queryKey: ["contactDetails", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => getContactDetails(identity, ns, signal),
  });
  const reload = () => void qc.invalidateQueries({ queryKey: ["contactDetails", ns] });
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Mobile verification.
  const [mobileReq, setMobileReq] = useState("");
  const [mobileCode, setMobileCode] = useState("");
  const mobileTimer = useResendTimer();

  // Add a mobile (a login without one).
  const [newMobile, setNewMobile] = useState("");
  const [newMobileReq, setNewMobileReq] = useState("");
  const [newMobileSentTo, setNewMobileSentTo] = useState("");
  const [newMobileCode, setNewMobileCode] = useState("");
  const newMobileTimer = useResendTimer();

  // Email add / change.
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailReq, setEmailReq] = useState("");
  const [emailSentTo, setEmailSentTo] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const emailTimer = useResendTimer();

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } catch (err) {
      setNotice({ severity: "error", text: contactErrorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const sendMobileCode = () =>
    run(async () => {
      const c = await sendAccountCode(identity, ns, ["sms"]);
      setMobileReq(c.requestId);
      setMobileCode("");
      mobileTimer.start(c.resendIn);
    });

  const confirmMobile = (e: FormEvent) => {
    e.preventDefault();
    const mobile = contact.data?.mobile ?? "";
    void run(async () => {
      try {
        await verifyOwnMobile(identity, ns, mobile, testCodeAccessToken(mobileReq, mobileCode));
      } finally {
        // A code is single-use: after any answer only a new code helps.
        setMobileCode("");
      }
      setMobileReq("");
      forgetTrustTokens(); // radaccess ended every trusted browser too
      setNotice({ severity: "success", text: "Mobile number verified." });
      reload();
    });
  };

  const sendNewMobileCode = () =>
    run(async () => {
      const e164 = normalizeIndianMobile(newMobile) ?? "";
      const c = await sendAddMobileCode(identity, ns, e164);
      setNewMobileReq(c.requestId);
      setNewMobileSentTo(c.mobile || maskMobile(e164));
      setNewMobileCode("");
      newMobileTimer.start(c.resendIn);
    });

  const confirmNewMobile = (e: FormEvent) => {
    e.preventDefault();
    const e164 = normalizeIndianMobile(newMobile) ?? "";
    void run(async () => {
      try {
        await addOwnMobile(identity, ns, e164, newMobileReq, newMobileCode);
      } finally {
        setNewMobileCode("");
      }
      setNewMobileReq("");
      setNewMobile("");
      forgetTrustTokens(); // radaccess ended every trusted browser too
      setNotice({ severity: "success", text: "Mobile number added and verified." });
      reload();
    });
  };

  const sendEmailCode = () =>
    run(async () => {
      const c = await sendEmailCheckCode(identity, ns, newEmail);
      setEmailReq(c.requestId);
      setEmailSentTo(c.email || newEmail.trim());
      setEmailCode("");
      emailTimer.start(c.resendIn);
    });

  const confirmEmail = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      try {
        await setOwnEmail(identity, ns, newEmail, emailReq, emailCode);
      } finally {
        setEmailCode("");
      }
      setEmailReq("");
      setEditingEmail(false);
      setNewEmail("");
      forgetTrustTokens(); // radaccess ended every trusted browser too
      setNotice({ severity: "success", text: "Email address verified. You can now choose Email or Both for codes." });
      reload();
    });
  };

  const cancelEmail = () => {
    setEditingEmail(false);
    setNewEmail("");
    setEmailReq("");
    setEmailCode("");
    setNotice(null);
  };

  const d = contact.data;
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }} data-testid="contact-details">
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          My contact details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sign-in codes go to these. A verified email lets you choose Email or Both on every code screen.
        </Typography>
        {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
        {contact.isLoading && <CircularProgress size={20} />}
        {contact.isError && <Alert severity="warning">Could not read your contact details: {contactErrorText(contact.error)}</Alert>}

        {d && (
          <>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle2" sx={{ minWidth: 64 }}>
                  Mobile
                </Typography>
                {d.mobile ? (
                  <>
                    <Typography variant="body1" data-testid="contact-mobile">
                      {d.mobile}
                    </Typography>
                    {verifiedChip(d.mobileVerified, "contact-mobile-status")}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None
                  </Typography>
                )}
              </Box>
              {d.mobile && !d.mobileVerified && (
                <form onSubmit={confirmMobile} noValidate>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {mobileReq && (
                      <Typography variant="body2">We sent a code by SMS to {maskMobile(d.mobile)}.</Typography>
                    )}
                    {mobileReq && codeInput(mobileCode, setMobileCode, "contact-mobile-code")}
                    <Stack direction="row" spacing={1}>
                      {mobileReq && (
                        <Button type="submit" variant="contained" disabled={busy || mobileCode.length !== 6}>
                          Verify
                        </Button>
                      )}
                      <Button variant={mobileReq ? "text" : "contained"} onClick={() => void sendMobileCode()} disabled={busy || mobileTimer.left > 0}>
                        {mobileTimer.left > 0 ? `Resend code in ${mobileTimer.left}s` : mobileReq ? "Resend code" : "Verify by SMS code"}
                      </Button>
                    </Stack>
                  </Stack>
                </form>
              )}
              {/* Owner 2026-10-25: a login with no mobile adds one, proved by a code to it. */}
              {!d.mobile && (
                <form onSubmit={confirmNewMobile} noValidate>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField
                      label="Mobile number"
                      size="small"
                      value={newMobile}
                      onChange={(e) => { setNewMobile(e.target.value.replace(/[^\d+ ]/g, "")); setNewMobileReq(""); setNewMobileCode(""); }}
                      autoComplete="tel"
                      slotProps={{ htmlInput: { inputMode: "tel", "data-testid": "contact-mobile-input" } }}
                      sx={{ maxWidth: 240 }}
                    />
                    {newMobileReq && <Typography variant="body2">We sent a code by SMS to {newMobileSentTo}.</Typography>}
                    {newMobileReq && codeInput(newMobileCode, setNewMobileCode, "contact-new-mobile-code")}
                    <Stack direction="row" spacing={1}>
                      {newMobileReq && (
                        <Button type="submit" variant="contained" disabled={busy || newMobileCode.length !== 6}>
                          Add mobile
                        </Button>
                      )}
                      <Button
                        variant={newMobileReq ? "text" : "contained"}
                        onClick={() => void sendNewMobileCode()}
                        disabled={busy || normalizeIndianMobile(newMobile) === null || newMobileTimer.left > 0}
                      >
                        {newMobileTimer.left > 0 ? `Resend code in ${newMobileTimer.left}s` : newMobileReq ? "Resend code" : "Send code"}
                      </Button>
                    </Stack>
                  </Stack>
                </form>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {d.mobile ? "To use a different mobile number, ask your administrator." : "Every AnkhOS login needs a mobile number."}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle2" sx={{ minWidth: 64 }}>
                  Email
                </Typography>
                {d.email ? (
                  <>
                    <Typography variant="body1" data-testid="contact-email">
                      {d.email}
                    </Typography>
                    {verifiedChip(d.emailVerified, "contact-email-status")}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None
                  </Typography>
                )}
                {!editingEmail && (
                  <Button size="small" onClick={() => { setEditingEmail(true); setNotice(null); }} disabled={busy}>
                    {d.email ? (d.emailVerified ? "Change" : "Verify or change") : "Add email"}
                  </Button>
                )}
              </Box>
              {editingEmail && (
                <form onSubmit={confirmEmail} noValidate>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField
                      label="Email address"
                      type="email"
                      size="small"
                      value={newEmail}
                      onChange={(e) => { setNewEmail(e.target.value); setEmailReq(""); setEmailCode(""); }}
                      autoComplete="email"
                      slotProps={{ htmlInput: { "data-testid": "contact-email-input" } }}
                      sx={{ maxWidth: 360 }}
                    />
                    {emailReq && <Typography variant="body2">We emailed a code to {emailSentTo}. Check spam if it is not there.</Typography>}
                    {emailReq && codeInput(emailCode, setEmailCode, "contact-email-code")}
                    <Stack direction="row" spacing={1}>
                      {emailReq && (
                        <Button type="submit" variant="contained" disabled={busy || emailCode.length !== 6}>
                          Confirm
                        </Button>
                      )}
                      <Button
                        variant={emailReq ? "text" : "contained"}
                        onClick={() => void sendEmailCode()}
                        disabled={busy || !looksLikeEmail(newEmail) || emailTimer.left > 0}
                      >
                        {emailTimer.left > 0 ? `Resend code in ${emailTimer.left}s` : emailReq ? "Resend code" : "Send code"}
                      </Button>
                      <Button onClick={cancelEmail} disabled={busy}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </form>
              )}
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
